import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  message
} from 'antd';
import { EditOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { membershipAdminService } from '../../services/membershipAdmin';

const STATUSES = ['Đợi Duyệt', 'Hoạt Động', 'Hết hạn', 'Khóa'];

const getErrorMessage = (error, fallback) => (
  error?.response?.data?.message || error?.message || fallback
);

const statusColor = (status) => ({
  'Đợi Duyệt': 'gold',
  'Hoạt Động': 'green',
  'Hết hạn': 'orange',
  'Khóa': 'red'
}[status] || 'default');

const pick = (record, ...keys) => {
  for (const key of keys) {
    if (record?.[key] !== undefined && record?.[key] !== null) return record[key];
  }
  return undefined;
};

const formatVietnamDate = (value) => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat('vi-VN', {
    timeZone: 'Asia/Ho_Chi_Minh',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(date);
};

const renderText = (value) => {
  if (value === undefined || value === null || value === '') return '-';
  return (
    <Tooltip title={String(value)}>
      <span>{String(value)}</span>
    </Tooltip>
  );
};

const renderWebsite = (value) => {
  if (!value) return '-';
  const text = String(value).trim();

  if (/^https?:\/\//i.test(text)) {
    return (
      <Tooltip title={text}>
        <a href={text} target="_blank" rel="noreferrer">
          {text}
        </a>
      </Tooltip>
    );
  }

  return renderText(text);
};

const MemberManager = () => {
  const [form] = Form.useForm();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusChangingId, setStatusChangingId] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const result = await membershipAdminService.getMembers({
        search,
        trangThai: status,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize
      });

      if (!result?.success || !Array.isArray(result.data)) {
        throw new Error(result?.message || 'Dữ liệu danh sách thành viên không hợp lệ');
      }

      setMembers(result.data);
      setTotal(Number(result.pagination?.total || 0));
    } catch (error) {
      setMembers([]);
      setTotal(0);
      message.error(getErrorMessage(error, 'Không thể tải danh sách thành viên'));
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, search, status]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const openEdit = (record) => {
    setEditingMember(record);
    form.setFieldsValue({
      tenDoanhNghiep: pick(record, 'TenDoanhNghiep', 'tenDoanhNghiep') || '',
      nguoiDaiDien: pick(record, 'NguoiDaiDien', 'nguoiDaiDien') || '',
      chucVu: pick(record, 'ChucVu', 'TrucVuNguoiDaiDien', 'trucVuNguoiDaiDien', 'chucVu') || '',
      idLinhVuc: pick(record, 'IDLinhVuc', 'idLinhVuc') ?? null,
      chiTiet: pick(record, 'ChiTiet', 'chiTiet') || '',
      linkWeb: pick(record, 'LinkWeb', 'linkWeb') || '',
      hotline: pick(record, 'Hotline', 'hotline') || '',
      diaChi: pick(record, 'DiaChi', 'diaChi') || '',
      trangThai: pick(record, 'TrangThai', 'trangThai') || 'Đợi Duyệt'
    });
  };

  const closeEdit = () => {
    if (saving) return;
    setEditingMember(null);
    form.resetFields();
  };

  const saveMember = async () => {
    try {
      const values = await form.validateFields();
      const userId = pick(editingMember, 'IDUser', 'IdUser', 'idUser', 'id');
      if (!userId) throw new Error('Không xác định được ID thành viên');

      const payload = {
        tenDoanhNghiep: values.tenDoanhNghiep.trim(),
        nguoiDaiDien: values.nguoiDaiDien.trim(),
        chucVu: values.chucVu.trim(),
        idLinhVuc: values.idLinhVuc ?? null,
        chiTiet: values.chiTiet?.trim() || null,
        linkWeb: values.linkWeb?.trim() || null,
        hotline: values.hotline?.trim() || null,
        diaChi: values.diaChi?.trim() || null,
        trangThai: values.trangThai
      };

      setSaving(true);
      const result = await membershipAdminService.updateMember(userId, payload);
      if (result?.success === false) throw new Error(result.message || 'Không thể cập nhật thành viên');

      message.success(result?.message || 'Cập nhật thành viên thành công');
      setEditingMember(null);
      form.resetFields();
      fetchMembers();
    } catch (error) {
      if (!error?.errorFields) {
        message.error(getErrorMessage(error, 'Không thể cập nhật thành viên'));
      }
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (record, nextStatus) => {
    const userId = pick(record, 'IDUser', 'IdUser', 'idUser', 'id');
    if (!userId || nextStatus === pick(record, 'TrangThai', 'trangThai')) return;

    setStatusChangingId(userId);
    try {
      const result = await membershipAdminService.updateMemberStatus(userId, nextStatus);
      if (result?.success === false) throw new Error(result.message || 'Không thể đổi trạng thái');
      message.success(result?.message || 'Đổi trạng thái thành công');
      fetchMembers();
    } catch (error) {
      message.error(getErrorMessage(error, 'Không thể đổi trạng thái'));
    } finally {
      setStatusChangingId(null);
    }
  };

  const columns = [
    {
      title: 'ID',
      key: 'id',
      width: 95,
      fixed: 'left',
      render: (_, record) => pick(record, 'IDUser', 'IdUser', 'idUser', 'id')
    },
    {
      title: 'Doanh nghiệp',
      key: 'business',
      width: 220,
      fixed: 'left',
      ellipsis: true,
      render: (_, record) => renderText(pick(record, 'TenDoanhNghiep', 'tenDoanhNghiep'))
    },
    {
      title: 'Người đại diện',
      key: 'representative',
      width: 180,
      ellipsis: true,
      render: (_, record) => renderText(pick(record, 'NguoiDaiDien', 'nguoiDaiDien'))
    },
    {
      title: 'Chức vụ',
      key: 'position',
      width: 150,
      ellipsis: true,
      render: (_, record) => renderText(
        pick(record, 'ChucVu', 'TrucVuNguoiDaiDien', 'trucVuNguoiDaiDien', 'chucVu')
      )
    },
    {
      title: 'Ngành nghề / Lĩnh vực',
      key: 'industry',
      width: 190,
      ellipsis: true,
      render: (_, record) => {
        const industry = pick(record, 'NganhNghe', 'nganhNghe');
        if (industry) return renderText(industry);

        const fieldId = pick(record, 'IDLinhVuc', 'idLinhVuc');
        return fieldId ? `ID ${fieldId}` : '-';
      }
    },
    {
      title: 'Hotline',
      key: 'hotline',
      width: 130,
      render: (_, record) => renderText(pick(record, 'Hotline', 'hotline'))
    },
    {
      title: 'Website',
      key: 'website',
      width: 220,
      ellipsis: true,
      render: (_, record) => renderWebsite(pick(record, 'LinkWeb', 'linkWeb'))
    },
    {
      title: 'Địa chỉ',
      key: 'address',
      width: 250,
      ellipsis: true,
      render: (_, record) => renderText(pick(record, 'DiaChi', 'diaChi'))
    },
    {
      title: 'Giới thiệu',
      key: 'detail',
      width: 240,
      ellipsis: true,
      render: (_, record) => renderText(pick(record, 'ChiTiet', 'chiTiet'))
    },
    {
      title: 'Hạn thành viên',
      key: 'expiry',
      width: 145,
      fixed: 'right',
      render: (_, record) => formatVietnamDate(pick(record, 'HanTaiGiaNhap', 'hanTaiGiaNhap'))
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 190,
      fixed: 'right',
      render: (_, record) => {
        const userId = pick(record, 'IDUser', 'IdUser', 'idUser', 'id');
        const value = pick(record, 'TrangThai', 'trangThai');
        return (
          <Select
            value={value}
            style={{ width: 165 }}
            loading={statusChangingId === userId}
            disabled={statusChangingId !== null}
            onChange={(nextStatus) => changeStatus(record, nextStatus)}
            options={STATUSES.map((item) => ({
              value: item,
              label: <Tag color={statusColor(item)}>{item}</Tag>
            }))}
          />
        );
      }
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 105,
      fixed: 'right',
      render: (_, record) => (
        <Button type="primary" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
          Sửa
        </Button>
      )
    }
  ];

  return (
    <>
      <Space wrap style={{ marginBottom: 16 }}>
        <Input
          allowClear
          value={searchInput}
          prefix={<SearchOutlined />}
          placeholder="Tìm thông tin thành viên..."
          style={{ width: 300 }}
          onChange={(event) => setSearchInput(event.target.value)}
          onPressEnter={() => {
            setCurrentPage(1);
            setSearch(searchInput.trim());
          }}
        />
        <Select
          allowClear
          value={status}
          placeholder="Tất cả trạng thái"
          style={{ width: 180 }}
          options={STATUSES.map((item) => ({ value: item, label: item }))}
          onChange={(value) => {
            setStatus(value);
            setCurrentPage(1);
          }}
        />
        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={() => {
            setCurrentPage(1);
            setSearch(searchInput.trim());
          }}
        >
          Tìm kiếm
        </Button>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => {
            setSearchInput('');
            setSearch('');
            setStatus(undefined);
            setCurrentPage(1);
          }}
        >
          Đặt lại
        </Button>
      </Space>

      <Table
        rowKey={(record) => String(pick(record, 'IDUser', 'IdUser', 'idUser', 'id'))}
        columns={columns}
        dataSource={members}
        loading={loading}
        scroll={{ x: 2200 }}
        pagination={{
          current: currentPage,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          showTotal: (value) => `Tổng ${value} thành viên`,
          onChange: (page, nextPageSize) => {
            if (nextPageSize !== pageSize) {
              setPageSize(nextPageSize);
              setCurrentPage(1);
            } else {
              setCurrentPage(page);
            }
          }
        }}
      />

      <Modal
        title="Cập nhật thành viên"
        open={Boolean(editingMember)}
        okText="Cập nhật"
        cancelText="Hủy"
        confirmLoading={saving}
        onOk={saveMember}
        onCancel={closeEdit}
        width={760}
        forceRender
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Hạn thành viên">
            <Input
              disabled
              value={formatVietnamDate(
                pick(editingMember, 'HanTaiGiaNhap', 'hanTaiGiaNhap')
              )}
            />
          </Form.Item>
          <Form.Item name="tenDoanhNghiep" label="Tên doanh nghiệp" rules={[{ required: true, whitespace: true }]}> 
            <Input maxLength={250} />
          </Form.Item>
          <Space size="large" align="start" style={{ display: 'flex' }}>
            <Form.Item name="nguoiDaiDien" label="Người đại diện" style={{ flex: 1 }} rules={[{ required: true, whitespace: true }]}> 
              <Input maxLength={150} />
            </Form.Item>
            <Form.Item name="chucVu" label="Chức vụ" style={{ flex: 1 }} rules={[{ required: true, whitespace: true }]}> 
              <Input maxLength={150} />
            </Form.Item>
          </Space>
          <Space size="large" align="start" style={{ display: 'flex' }}>
            <Form.Item name="idLinhVuc" label="ID lĩnh vực" style={{ flex: 1 }}>
              <InputNumber min={1} precision={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="trangThai" label="Trạng thái" style={{ flex: 1 }} rules={[{ required: true }]}> 
              <Select options={STATUSES.map((item) => ({ value: item, label: item }))} />
            </Form.Item>
          </Space>
          <Form.Item name="hotline" label="Hotline"><Input maxLength={20} /></Form.Item>
          <Form.Item name="linkWeb" label="Website"><Input maxLength={250} placeholder="https://example.com" /></Form.Item>
          <Form.Item name="diaChi" label="Địa chỉ"><Input maxLength={250} /></Form.Item>
          <Form.Item name="chiTiet" label="Chi tiết"><Input.TextArea rows={4} maxLength={512} showCount /></Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default MemberManager;
